import { Config } from "../config";
import { store } from "./store";
import { logout } from "../pages/login/loginSlice";

export interface ApiFetchOptions extends Omit<RequestInit, "headers"> {
    token?: string | null;
    headers?: Record<string, string>;
    // Some endpoints (e.g. changing your password) can legitimately return a 401 to mean
    // "that wasn't right" rather than "your session is dead" - set this to false so the
    // caller gets the raw Response back instead of being logged out.
    treatAuthErrorsAsSessionExpiry?: boolean;
}

// Centralizes the API base URL and Authorization/Content-Type headers that
// were previously duplicated across FolderContents, PreviewPane and
// ComposeMailItem. Callers still own their own useEffect/AbortController/
// loading state - this only removes the copy-pasted URL/header construction.
//
// Also centralizes session-expiry handling: a 401, or a 403 from the API
// Gateway authorizer, means the JWT is dead, so we clear the persisted session
// (which also triggers Inbox's reactive redirect to /login) and throw a
// SessionExpiredError so callers can skip showing a misleading "failed to
// load" message, the same way they already skip it for AbortError.
//
// Our own Lambdas also answer 403 for things the user is simply not allowed
// to do (e.g. sending from an address they don't own). Those carry an
// { error } body and are handed back to the caller like any other failure.
export async function apiFetch(path: string, options: ApiFetchOptions = {}): Promise<Response> {
    const { token, headers, treatAuthErrorsAsSessionExpiry = true, ...rest } = options;
    const response = await fetch(Config.apiEndpoint + path, {
        mode: "cors",
        ...rest,
        headers: {
            "Content-Type": "application/json",
            ...(token ? { "Authorization": "Bearer " + token } : {}),
            ...headers
        }
    });

    if (treatAuthErrorsAsSessionExpiry && (response.status === 401 || (response.status === 403 && !(await isApplicationError(response))))) {
        store.dispatch(logout());
        const sessionError = new Error("Session expired");
        sessionError.name = "SessionExpiredError";
        throw sessionError;
    }

    return response;
}

// Authorizer denials come back from API Gateway as { Message: "User is not
// authorized ..." }; our handlers' own 403s always use an "error" key.
async function isApplicationError(response: Response): Promise<boolean> {
    try {
        const body = await response.clone().json();
        return typeof body?.error === "string";
    } catch {
        return false;
    }
}
